import { useEffect, useState } from 'react'
import { CheckCircle2, AlertTriangle, ExternalLink, RefreshCw } from 'lucide-react'
import { toast } from 'sonner'
import { formatDistanceToNow } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { SectionCard } from '@/components/shared/section-card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ChonkyButton } from '@/components/shared/chonky-button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { useIgStatus, useIgAuthUrl, useDisconnectIg } from '@/hooks/use-social-listening'

export function IgConnectionCard() {
  const { data: status, isLoading } = useIgStatus()
  const igAuthUrl = useIgAuthUrl()
  const disconnectIg = useDisconnectIg()

  // Handle post-OAuth redirect feedback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const connected = params.get('connected')
    const error = params.get('error')

    if (connected === 'true') {
      toast.success('Instagram conectado com sucesso!')
      window.history.replaceState({}, '', window.location.pathname)
    } else if (error) {
      toast.error(`Erro ao conectar: ${error}`)
      window.history.replaceState({}, '', window.location.pathname)
    }
  }, [])

  const [now] = useState(() => Date.now())

  const handleConnect = async () => {
    try {
      const result = await igAuthUrl.mutateAsync()
      window.location.href = result.authUrl
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao iniciar conexão'
      toast.error(message)
    }
  }

  if (isLoading) return null // PageSkeleton handles loading state

  const isConnected = status?.connected
  const expiresAt = status?.expiresAt ? new Date(status.expiresAt) : null
  const daysUntilExpiry = expiresAt
    ? Math.ceil((expiresAt.getTime() - now) / (1000 * 60 * 60 * 24))
    : null
  const isExpiringSoon = daysUntilExpiry !== null && daysUntilExpiry <= 7

  return (
    <SectionCard
      title="Instagram"
      description="Conecte sua conta para monitorar menções."
      actions={
        isConnected ? (
          <Badge variant={isExpiringSoon ? 'destructive' : 'default'}>
            {isExpiringSoon ? (
              <AlertTriangle className="h-3 w-3 mr-1" />
            ) : (
              <CheckCircle2 className="h-3 w-3 mr-1" />
            )}
            {isExpiringSoon ? 'Expirando' : 'Conectado'}
          </Badge>
        ) : (
          <Badge variant="secondary">Desconectado</Badge>
        )
      }
    >
      {isConnected ? (
        <div className="space-y-4">
          <div className="flex flex-col gap-1 text-sm">
            <div className="text-muted-foreground">
              ID da conta: <span className="text-foreground font-medium">{status?.username}</span>
            </div>
            {expiresAt && (
              <div
                className={
                  isExpiringSoon
                    ? 'text-destructive font-medium text-sm'
                    : 'text-muted-foreground text-sm'
                }
              >
                Token expira {formatDistanceToNow(expiresAt, { addSuffix: true, locale: ptBR })}
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {isExpiringSoon && (
              <Button
                variant="outline"
                size="sm"
                className="w-full sm:w-auto"
                onClick={handleConnect}
                disabled={igAuthUrl.isPending}
              >
                <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                Reconectar
              </Button>
            )}
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full sm:w-auto text-destructive hover:text-destructive"
                >
                  Desconectar
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-lg">
                <AlertDialogHeader>
                  <AlertDialogTitle>Desconectar Instagram?</AlertDialogTitle>
                  <AlertDialogDescription>
                    O monitoramento de menções será interrompido. Você pode reconectar a qualquer
                    momento.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={async () => {
                      try {
                        await disconnectIg.mutateAsync()
                        toast.success('Instagram desconectado')
                      } catch (err: unknown) {
                        const message = err instanceof Error ? err.message : 'Erro ao desconectar'
                        toast.error(message)
                      }
                    }}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Desconectar
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      ) : (
        <ChonkyButton onClick={handleConnect} loading={igAuthUrl.isPending}>
          <ExternalLink className="h-3.5 w-3.5" />
          Conectar Instagram
        </ChonkyButton>
      )}
    </SectionCard>
  )
}
